import React, { useEffect, useState } from 'react';
import { AlertCircle, RefreshCw, Sparkles, Clock, FileText } from 'lucide-react';
import { usePlanGeneration, getProgressMessage } from '../hooks/usePlanGeneration';
import { aiIntelligenceService, PlanSummary } from '../services/aiIntelligenceService';

interface PlanGenerationProps {
  teamId: string;
  onPlanReady: (planId: string) => void;
}

export function PlanGeneration({ teamId, onPlanReady }: PlanGenerationProps) {
  const {
    phase,
    jobState,
    planId,
    errorMessage,
    retryable,
    startGeneration,
    retry,
    reset,
  } = usePlanGeneration();

  const [elapsedMs, setElapsedMs] = useState(0);
  const [previousPlans, setPreviousPlans] = useState<PlanSummary[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Fetch previously generated plans
  useEffect(() => {
    let cancelled = false;
    const fetchPlans = async () => {
      try {
        const plans = await aiIntelligenceService.listPlans(teamId);
        if (!cancelled) {
          setPreviousPlans(plans);
        }
      } catch {
        // Silently fail — plans list is non-critical
      } finally {
        if (!cancelled) {
          setPlansLoading(false);
        }
      }
    };
    fetchPlans();
    return () => { cancelled = true; };
  }, [teamId]);

  // Elapsed time ticker for progress messages
  useEffect(() => {
    if (phase !== 'polling' && phase !== 'submitting') {
      setElapsedMs(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  // Transition to plan display when completed
  useEffect(() => {
    if (phase === 'completed' && planId) {
      onPlanReady(planId);
    }
  }, [phase, planId, onPlanReady]);

  const handleGenerate = () => {
    startGeneration(teamId);
  };

  const handleLoadPlan = (selectedPlanId: string) => {
    onPlanReady(selectedPlanId);
  };

  // Loading/polling state
  if (phase === 'submitting' || phase === 'polling') {
    const progressMessage = getProgressMessage(jobState, elapsedMs);
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <div className="w-10 h-10 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">
          Generating Your Development Plan
        </h2>
        <p className="text-gray-600 text-sm mb-2">
          {progressMessage}
        </p>
        <p className="text-xs text-gray-400">
          This usually takes 30-60 seconds
        </p>

        {/* Progress dots animation */}
        <div className="flex items-center justify-center gap-1.5 mt-6">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    );
  }

  // Timeout state
  if (phase === 'timeout') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-yellow-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Taking Longer Than Expected
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {errorMessage || 'Plan generation is taking longer than expected. Please try again.'}
        </p>
        <button
          onClick={retry}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors min-h-[44px]"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  // Error/failed state
  if (phase === 'failed') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Generation Failed
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {errorMessage || 'Something went wrong while generating your plan.'}
        </p>
        <div className="flex items-center justify-center gap-3">
          {retryable && (
            <button
              onClick={retry}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors min-h-[44px]"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          )}
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors min-h-[44px]"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // Idle state — show generate button and previous plans
  return (
    <div className="max-w-2xl mx-auto py-8">
      {/* Generate new plan section */}
      <div className="text-center mb-10">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Ready to Generate
        </h2>
        <p className="text-gray-600 text-sm mb-6">
          Your coaching DNA is defined and all requirements are met. Generate a personalised development plan based on your team's assessment data and coaching philosophy.
        </p>
        <button
          onClick={handleGenerate}
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-lg hover:shadow-xl min-h-[48px]"
        >
          <Sparkles className="w-4 h-4" />
          Generate Development Plan
        </button>
      </div>

      {/* Previous plans section */}
      {!plansLoading && previousPlans.length > 0 && (
        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Previous Plans
          </h3>
          <div className="space-y-3">
            {previousPlans.map((plan) => (
              <button
                key={plan.planId}
                onClick={() => handleLoadPlan(plan.planId)}
                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {plan.overview?.summary || 'Development Plan'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(plan.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {plan.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
